/**
 * Utilities for detecting and handling synthetic Gaia action messages
 * in Oracle chat conversations.
 *
 * Synthetic messages use the format:
 *   <Gaia action="action_name" text="Display text" />
 *
 * The `text` attribute (or `question` as a fallback) is displayed as a
 * visible user message. When both are present, `text` wins. Messages
 * without either attribute are hidden entirely.
 *
 * Action names are snake_case.
 */

// Matches self-closing Gaia tags: <Gaia ... />
const GAIA_ACTION_TAG_RE = /^<Gaia\s[^>]*action="([^"]+)"[^>]*\/>\s*$/

/**
 * Extracts the action name from a synthetic Gaia message.
 * Returns null if the message is not a Gaia action tag.
 */
export const getGaiaAction = (text: string): string | null => {
  const match = text.trim().match(GAIA_ACTION_TAG_RE)
  return match ? match[1] : null
}

/**
 * Extracts the display text from a synthetic Gaia message.
 * Prefers the `text` attribute; falls back to `question` when `text`
 * is absent. Returns null if neither attribute is present.
 */
export const getGaiaDisplayText = (text: string): string | null => {
  const textMatch = text.match(/text="([^"]*)"/)
  if (textMatch) return textMatch[1]

  const questionMatch = text.match(/question="([^"]*)"/)
  return questionMatch ? questionMatch[1] : null
}

/**
 * Determines whether a user message should be hidden in the conversation.
 *
 * Per the Oracle Cards spec: `<Gaia action='oracle_begin' />` is not
 * rendered as a visible user message — the agent's response appears
 * as the first visible content in the conversation.
 */
export const isHiddenSyntheticMessage = (text: string): boolean => {
  const action = getGaiaAction(text)
  if (!action) return false

  // oracle_begin and tarot_begin without display text are hidden
  if (action === 'oracle_begin' || action === 'tarot_begin') {
    return getGaiaDisplayText(text) === null
  }

  return false
}

/**
 * Returns the text to display for a user message. For synthetic Gaia
 * messages with a `text` attribute (e.g., oracle_pull) or a `question`
 * attribute (e.g., tarot_pull), returns the display text. For regular
 * messages, returns the original text.
 */
export const getUserDisplayText = (text: string): string => {
  const action = getGaiaAction(text)
  if (!action) return text

  const displayText = getGaiaDisplayText(text)
  return displayText || text
}
